import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { logger } from "../services/logger.service";

export type SolutionMode =
  | "default"
  | "senior_living"
  | "healthcare"
  | "provider_groups"
  | "health_plan";

interface ThemeContextValue {
  solutionMode: SolutionMode;
  setSolutionMode: (mode: SolutionMode) => void;
  isAutoDetected: boolean;
  primaryColor: string;
  themeName: string;
}

const STORAGE_KEY = "skyhire-solution-mode";

const THEME_CONFIG: Record<SolutionMode, { primaryColor: string; themeName: string }> = {
  default: {
    primaryColor: "#0078d4",
    themeName: "Default",
  },
  healthcare: {
    primaryColor: "#2E7D32",
    themeName: "Healthcare",
  },
  senior_living: {
    primaryColor: "#00BCD4",
    themeName: "Senior Living & SNFs",
  },
  provider_groups: {
    primaryColor: "#6B4EAD",
    themeName: "Provider Groups",
  },
  health_plan: {
    primaryColor: "#E91E63",
    themeName: "Health Plans",
  },
};

const VALID_MODES: SolutionMode[] = [
  "default",
  "senior_living",
  "healthcare",
  "provider_groups",
  "health_plan",
];

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isValidSolutionMode(value: string): value is SolutionMode {
  return VALID_MODES.includes(value as SolutionMode);
}

function getStoredSolutionMode(): SolutionMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidSolutionMode(stored)) {
      return stored;
    }
  } catch {
    logger.warn("Failed to load solution mode from localStorage");
  }
  return null;
}

function saveSolutionMode(mode: SolutionMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    logger.warn("Failed to save solution mode to localStorage");
  }
}

function detectSolutionModeFromCapabilities(capabilities: string[]): SolutionMode {
  if (capabilities.includes("health_plan")) {
    return "health_plan";
  }
  if (capabilities.includes("provider_groups")) {
    return "provider_groups";
  }
  if (capabilities.includes("healthcare")) {
    return "healthcare";
  }
  if (capabilities.includes("senior_living")) {
    return "senior_living";
  }
  return "default";
}

function applySolutionModeStyles(mode: SolutionMode): void {
  const root = document.documentElement;
  const config = THEME_CONFIG[mode];

  root.classList.remove(
    "theme-default",
    "theme-healthcare",
    "theme-senior-living",
    "theme-provider-groups",
    "theme-health-plan",
  );
  root.classList.add(`theme-${mode.replace(/_/g, "-")}`);

  root.style.setProperty("--solution-primary-color", config.primaryColor);
  root.style.setProperty("--solution-primary-color-light", `${config.primaryColor}20`);
  root.style.setProperty(
    "--solution-primary-color-hover",
    adjustColorBrightness(config.primaryColor, -10),
  );
}

function adjustColorBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}

interface ThemeProviderProps {
  children: ReactNode;
  tenantCapabilities?: string[];
}

export function ThemeProvider({ children, tenantCapabilities = [] }: ThemeProviderProps) {
  const [solutionMode, setSolutionModeState] = useState<SolutionMode>(() => {
    return getStoredSolutionMode() || "default";
  });
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  useEffect(() => {
    if (tenantCapabilities.length > 0) {
      const storedMode = getStoredSolutionMode();
      if (!storedMode) {
        const detectedMode = detectSolutionModeFromCapabilities(tenantCapabilities);
        setSolutionModeState(detectedMode);
        setIsAutoDetected(true);
      }
    }
  }, [tenantCapabilities]);

  useEffect(() => {
    applySolutionModeStyles(solutionMode);
  }, [solutionMode]);

  const setSolutionMode = useCallback((mode: SolutionMode) => {
    setSolutionModeState(mode);
    saveSolutionMode(mode);
    setIsAutoDetected(false);
  }, []);

  const config = THEME_CONFIG[solutionMode];

  const value: ThemeContextValue = {
    solutionMode,
    setSolutionMode,
    isAutoDetected,
    primaryColor: config.primaryColor,
    themeName: config.themeName,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export function useThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}

export function useSolutionMode(): SolutionMode {
  const { solutionMode } = useTheme();
  return solutionMode;
}

export { THEME_CONFIG };
export default ThemeContext;
